import type {
  IntervalOrigin,
  ReportDocumentOptions,
  ViewType,
} from "@digits-graphql/frontend/graphql-bearer"
import type { PreferenceValueMap } from "src/shared/config/preferenceDefinitions"
import { buildPreferenceKeys, definePreference } from "src/shared/config/preferenceDefinitions"

type PersistedStatementOptions = Record<string, ReportDocumentOptions>
// Loosely typed on purpose: the Budgets app validates each field when it reads them back.
type PersistedBudgetVsActualsSettings = Record<string, string | boolean>

export const legalEntityPreferenceDefinitions = {
  paymentVerificationNotStartedBannerDismissed: definePreference<boolean>()({
    key: "payment-verification-not-started-banner-dismissed",
    kind: "boolean",
    defaultValue: false,
  }),
  paymentVerificationPendingBannerDismissed: definePreference<boolean>()({
    key: "payment-verification-pending-banner-dismissed",
    kind: "boolean",
    defaultValue: false,
  }),
  paymentVerificationActionNeededBannerDismissed: definePreference<boolean>()({
    key: "payment-verification-action-needed-banner-dismissed",
    kind: "boolean",
    defaultValue: false,
  }),
  onboardingHomeTransitionSeen: definePreference<boolean>()({
    key: "onboarding-home-transition-seen",
    kind: "boolean",
    defaultValue: false,
  }),
  finishSetupDismissed: definePreference<boolean>()({
    key: "finish-setup-dismissed",
    kind: "boolean",
    defaultValue: false,
  }),
  // Loosely typed on purpose: setup's debug drawer checks the shape when it reads it back.
  setupProgressOverride: definePreference<Record<string, unknown> | undefined>()({
    key: "setup-progress-override",
    kind: "json",
  }),
  checklistOrigin: definePreference<{ origin: IntervalOrigin; timestamp: number } | undefined>()({
    key: "checklist-origin",
    kind: "json",
  }),
  inboxOrigin: definePreference<{ origin: IntervalOrigin; timestamp: number } | undefined>()({
    key: "inbox-origin",
    kind: "json",
  }),
  financialsStatementOptions: definePreference<PersistedStatementOptions | undefined>()({
    key: "financials-statement-options",
    kind: "json",
  }),
  budgetVsActualsSettings: definePreference<PersistedBudgetVsActualsSettings | undefined>()({
    key: "budget-vs-actuals-settings",
    kind: "json",
  }),
  viewType: definePreference<ViewType | undefined>()({
    key: "view-type",
    kind: "string",
  }),
} as const

export const legalEntityPreferenceKeys = buildPreferenceKeys(legalEntityPreferenceDefinitions)

export type LegalEntityPreferenceValueMap = PreferenceValueMap<
  typeof legalEntityPreferenceDefinitions
>
